import { formatTimeRange, unixToISO } from "./tooltips";

function severityFromValue(value, maxValue, higherIsWorse) {
    if (!Number.isFinite(value) || !Number.isFinite(maxValue) || maxValue <= 0) return null;
    let ratio = value / maxValue;
    if (!higherIsWorse) {
        ratio = 1 - ratio;
    }
    ratio = Math.max(0, Math.min(1, ratio));
    if (ratio >= 0.8) return { severity: "red", score: ratio };
    if (ratio >= 0.5) return { severity: "orange", score: ratio };
    return null;
}

function downgradeSeverity(severity, steps) {
    if (!severity || !steps) return severity;
    const ladder = ["red", "orange", "info"];
    const idx = ladder.indexOf(severity);
    if (idx === -1) return severity;
    const next = Math.min(idx + steps, ladder.length - 1);
    return ladder[next];
}

function severityRank(severity) {
    if (severity === "red") return 2;
    if (severity === "orange") return 1;
    if (severity === "info") return 0;
    return -1;
}

export function buildHeatmapIssues({
    rows,
    metricLabel,
    maxValue,
    higherIsWorse = true,
    topN = 3,
    formatValue = (v) => String(v),
    includeRowName = true,
    valueSelector = null,
    severityDowngrade = 0,
}) {
    const candidates = [];
    (rows || []).forEach((row) => {
        const rawVals = valueSelector ? valueSelector(row) : row?.vals;
        const vals = Array.isArray(rawVals) ? rawVals : [];
        const ranges = Array.isArray(row?.ranges) ? row.ranges : [];
        for (let i = 0; i < vals.length; i++) {
            const value = vals[i];
            const severityInfo = severityFromValue(value, maxValue, higherIsWorse);
            if (!severityInfo) continue;
            const severity = downgradeSeverity(severityInfo.severity, severityDowngrade);
            if (!severity) continue;
            const range = ranges[i] || null;
            const timeRange = range && Number.isFinite(range.start) && Number.isFinite(range.end)
                ? formatTimeRange(range.start, range.end)
                : null;
            const detailsParts = [];
            if (includeRowName && row?.name) {
                detailsParts.push(row.name);
            }
            detailsParts.push(formatValue(value));
            candidates.push({
                severity,
                score: severityInfo.score,
                time_range: timeRange,
                issue: `${metricLabel} ${higherIsWorse ? "high" : "low"}`,
                details: detailsParts.join(" — "),
                // Per-row heatmaps know which site/AP/circuit the bucket
                // belongs to; this lets the explain aggregation count
                // distinct affected nodes.
                node: includeRowName && row?.name ? String(row.name) : null,
                node_kind: row?.type ? String(row.type) : null,
                // Additive structured hint: the bucket's time window in the
                // same shape as executive_dashlets, so the backend can label it.
                window: range && Number.isFinite(range.start) && Number.isFinite(range.end)
                    ? { start: unixToISO(range.start), end: unixToISO(range.end) }
                    : null,
            });
        }
    });

    candidates.sort((a, b) => {
        const rankA = severityRank(a.severity);
        const rankB = severityRank(b.severity);
        if (rankA !== rankB) return rankB - rankA;
        if (b.score !== a.score) return b.score - a.score;
        return 0;
    });

    return candidates.slice(0, topN).map((c) => ({
        time_range: c.time_range,
        severity: c.severity,
        issue: c.issue,
        details: c.details,
        node: c.node,
        node_kind: c.node_kind,
        window: c.window,
    }));
}
